import { Group, LoadingManager, SRGBColorSpace } from "three";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import { TGALoader } from "three/examples/jsm/loaders/TGALoader.js";
import { validateBytes } from "gltf-validator";
import { convertFbxMaterial as convertViewerFbxMaterial } from "../logic/fbx_materials.js";
import { readGlb, writeGlb } from "./glb.js";
import { loadLegacyFbx } from "./ufbx-scene.js";

const textureExtensions = /\.(png|jpe?g|webp|bmp|tga)$/i;
const missingImage = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+jRZkAAAAASUVORK5CYII=";
export const resourceName = value => String(value ?? "").replaceAll("\\", "/").split("/").at(-1).toLowerCase();

export function checkFbxVersion(buffer) {
    const header = new TextDecoder().decode(new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 65536)));
    const binary = header.startsWith("Kaydara FBX Binary  \0\x1a\0");
    const version = binary && buffer.byteLength >= 27
        ? new DataView(buffer).getUint32(23, true)
        : Number(header.match(/\bFBXVersion\s*:\s*(\d+)/)?.[1]);
    return { version: version || null, binary, legacy: Boolean(version && version < (binary ? 6400 : 7000)) };
}

export function selectImportFiles(files) {
    const models = files.filter(file => /\.(glb|fbx)$/i.test(file.name));
    if (models.length !== 1) throw new Error("Choose one GLB or FBX model at a time. You can include texture images with an FBX.");
    const model = models[0], textures = files.filter(file => file !== model);
    if (/\.glb$/i.test(model.name) && textures.length) throw new Error("GLB resources must be embedded. Choose the GLB by itself.");
    const names = new Set();
    for (const file of textures) {
        if (!textureExtensions.test(file.name)) throw new Error(`Unsupported companion file: ${file.name}. Use PNG, JPEG, WebP, BMP or TGA textures.`);
        const name = resourceName(file.name);
        if (names.has(name)) throw new Error(`Two textures have the same filename: ${file.name}. Give them unique names before importing.`);
        names.add(name);
    }
    return { model, textures };
}

// FBX Phong/Lambert materials have no exact metallic/roughness equivalent.
export function convertFbxMaterial(source, warnings = new Set()) {
    const copy = source.clone();
    const target = convertViewerFbxMaterial(copy);
    if (target !== copy) copy.dispose();
    for (const key of ["alphaTest", "vertexColors", "normalMapType"]) if (source[key] !== undefined) target[key] = source[key];
    // These channels need baking or extensions not supported by the GLB viewer.
    target.alphaMap = null; target.bumpMap = null; target.displacementMap = null; target.envMap = null;
    for (const key of ["bumpMap", "displacementMap", "specularMap", "envMap"]) {
        if (source[key]) warnings.add(`${source.name || "Unnamed material"}: ${key} could not be represented in the converted PBR material.`);
    }
    return target;
}

function imageCanvas(image) {
    const canvas = document.createElement("canvas"); canvas.width = image.width; canvas.height = image.height;
    const context = canvas.getContext("2d");
    if (image.data) context.putImageData(new ImageData(new Uint8ClampedArray(image.data), image.width, image.height), 0, 0);
    else context.drawImage(image, 0, 0);
    return canvas;
}

function bakeOpacity(source, target, warnings) {
    if (!source.alphaMap) return;
    const alpha = source.alphaMap, base = target.map;
    alpha.updateMatrix(); base?.updateMatrix();
    if (base && (!alpha.matrix.equals(base.matrix) || alpha.channel !== base.channel || alpha.wrapS !== base.wrapS || alpha.wrapT !== base.wrapT || alpha.flipY !== base.flipY)) {
        warnings.add(`${source.name || "Unnamed material"}: the opacity texture uses different UV mapping; only scalar opacity was retained.`);
        return;
    }
    const canvas = imageCanvas(base?.image ?? alpha.image), context = canvas.getContext("2d");
    if (!base) { context.fillStyle = "white"; context.fillRect(0, 0, canvas.width, canvas.height); }
    const mask = document.createElement("canvas"); mask.width = canvas.width; mask.height = canvas.height;
    const maskContext = mask.getContext("2d"); maskContext.drawImage(imageCanvas(alpha.image), 0, 0, mask.width, mask.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height), opacity = maskContext.getImageData(0, 0, mask.width, mask.height);
    for (let i = 0; i < pixels.data.length; i += 4) pixels.data[i + 3] = Math.round(pixels.data[i + 3] * opacity.data[i + 1] / 255);
    context.putImageData(pixels, 0, 0);
    target.map = (base ?? alpha).clone(); target.map.image = canvas; target.map.colorSpace = SRGBColorSpace; target.map.needsUpdate = true;
}

export function normalizeFbxAnimations(animations) {
    for (const clip of animations) for (const track of clip.tracks) {
        // FBX time ticks can collapse to the same float32 time in glTF.
        // Retain the final value at that timestamp, including the last key.
        const times = [], values = [], stride = track.getValueSize();
        for (let i = 0; i < track.times.length; i++) {
            const time = Math.fround(track.times[i]);
            if (times.length && time === times.at(-1)) { times.pop(); values.splice(values.length - stride, stride); }
            times.push(time);
            for (let j = 0; j < stride; j++) values.push(track.values[i * stride + j]);
        }
        track.times = new track.TimeBufferType(times); track.values = new track.ValueBufferType(values);
    }
}

export function normalizeFbxSkinRoots(json) {
    const parents = new Map();
    json.nodes?.forEach((node, index) => node.children?.forEach(child => parents.set(child, index)));
    for (const skin of json.skins ?? []) {
        if (skin.skeleton === undefined) continue;
        const descendsFrom = joint => {
            const seen = new Set();
            for (let node = joint; node !== undefined && !seen.has(node); node = parents.get(node)) {
                if (node === skin.skeleton) return true;
                seen.add(node);
            }
            return false;
        };
        // GLTFExporter assumes bones[0] is the shared root; FBX can list sibling
        // bones first. This optional hint is unnecessary for the actual skin.
        if (!skin.joints.every(descendsFrom)) delete skin.skeleton;
    }
}

export async function convertFbx(file, textureFiles = [], onProgress = () => {}) {
    const warnings = new Set(["Check the imported orientation; FBX axis conventions may need adjustment in the source application.", "FBX materials are approximated as PBR: diffuse, normal, emissive, ambient occlusion and compatible opacity textures are retained. Shininess becomes roughness; metalness starts at 0. Procedural/custom shaders, layered textures and unsupported material channels require manual adjustment."]);
    const urls = new Set(), resources = new Map(), failed = new Set(), originals = new Set(), converted = new Set(), textures = new Set();
    let scene, legacy = false;
    const manager = new LoadingManager();
    manager.addHandler(/\.tga$/i, new TGALoader(manager));
    manager.setURLModifier(url => {
        if (url.startsWith("blob:")) { urls.add(url); return url; }
        if (url.startsWith("data:")) return url;
        const name = resourceName(url);
        if (resources.has(name)) return resources.get(name);
        warnings.add(`Missing texture: ${name || "unnamed image"}. Select it together with the FBX when importing.`);
        // Never fetch a path from the FBX on the network or local server.
        return missingImage;
    });
    manager.onError = url => { failed.add(url); warnings.add("A texture could not be decoded and was omitted. Use PNG, JPEG, WebP, BMP or TGA images."); };
    const ready = new Promise(resolve => { manager.onLoad = resolve; });
    try {
        for (const texture of textureFiles) {
            const url = URL.createObjectURL(texture); urls.add(url); resources.set(resourceName(texture.name), url);
        }
        onProgress("Reading FBX geometry and textures…");
        const buffer = await file.arrayBuffer();
        legacy = checkFbxVersion(buffer).legacy;
        manager.itemStart("fbx-import");
        try {
            if (legacy) {
                onProgress("Reading legacy FBX with ufbx…");
                scene = await loadLegacyFbx(buffer, manager, warnings, urls);
            } else scene = new FBXLoader(manager).parse(buffer, "");
        }
        finally { manager.itemEnd("fbx-import"); }
        await ready;
        let meshCount = 0;
        scene.traverse(node => {
            if (node.isMesh) meshCount++;
            for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
                if (!material) continue;
                originals.add(material);
                for (const value of Object.values(material)) if (value?.isTexture) textures.add(value);
            }
            // FBX loader bookkeeping is not portable glTF application metadata.
            delete node.userData.transformData;
        });
        if (!meshCount) throw new Error("The FBX does not contain mesh geometry. Import a model rather than an animation-only or material-only FBX.");
        onProgress("Converting materials and embedding textures…");
        const materialMap = new Map();
        for (const source of originals) {
            for (const [key, value] of Object.entries(source)) {
                if (!value?.isTexture) continue;
                if (!value.image?.width || !value.image?.height || value.image.src === missingImage || failed.has(value.image.src)) {
                    source[key] = null;
                    warnings.add(`${source.name || "Unnamed material"}: unavailable ${key} was omitted.`);
                }
            }
            const target = convertFbxMaterial(source, warnings); converted.add(target);
            bakeOpacity(source, target, warnings);
            if (target.map) textures.add(target.map);
            materialMap.set(source, target);
        }
        scene.traverse(node => {
            if (node.material) node.material = Array.isArray(node.material) ? node.material.map(material => materialMap.get(material)) : materialMap.get(node.material);
        });
        // FBX units are centimetres per unit; glTF uses metres. A wrapper also
        // scales animated translation tracks and skinning without rewriting them.
        const declaredUnits = scene.userData.unitScaleFactor ?? scene.parent?.userData.unitScaleFactor;
        const units = Number.isFinite(declaredUnits) && declaredUnits > 0 ? declaredUnits : 1;
        if (declaredUnits === undefined) warnings.add("FBX units were unspecified; centimetres were assumed. Check the model’s scale.");
        const root = new Group(); root.name = file.name.replace(/\.fbx$/i, ""); root.scale.setScalar(units / 100); root.add(scene);
        root.updateMatrixWorld(true);
        const animations = scene.animations ?? [];
        normalizeFbxAnimations(animations);
        onProgress("Creating and validating GLB…");
        const exported = readGlb(await new GLTFExporter().parseAsync(root, { binary: true, animations, onlyVisible: false, trs: true }));
        normalizeFbxSkinRoots(exported.json);
        const bytes = writeGlb(exported, exported.json);
        const validation = await validateBytes(bytes, { maxIssues: 100 });
        if (validation.issues.numErrors) throw new Error("The converted FBX could not produce a valid GLB: " + validation.issues.messages.filter(issue => issue.severity === 0).map(issue => issue.message).join(" "));
        validation.issues.messages.filter(issue => issue.severity === 1).forEach(issue => warnings.add(issue.message));
        return { bytes, filename: file.name.replace(/\.fbx$/i, ".glb"), report: {
            source: file.name, parser: legacy ? "ufbx" : "Three.js", meshes: meshCount, materials: materialMap.size, animations: animations.length,
            warnings: [...warnings], summary: `Converted ${file.name} to GLB${legacy ? " using ufbx" : ""} · ${meshCount} meshes · ${materialMap.size} PBR materials · ${animations.length} animation clips`
        } };
    } finally {
        scene?.traverse(node => { node.geometry?.dispose(); node.skeleton?.dispose(); });
        originals.forEach(material => material.dispose()); converted.forEach(material => material.dispose()); textures.forEach(texture => texture.dispose());
        urls.forEach(url => URL.revokeObjectURL(url));
    }
}
